import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SharePointConnectCard from '../../components/SharePointIntake/SharePointConnectCard.jsx';
import StatusPill from '../../components/SharePointIntake/StatusPill.jsx';

const API = process.env.REACT_APP_API_URL || '';

/**
 * Org-admin standalone page: /admin/sharepoint-intake
 *
 * Org Superadmins (and Platform Superusers, scoped) see the live state of
 * their SharePoint intake and can manage it without needing PCG support.
 *
 * Composed of:
 *   - SharePointConnectCard — the same component the wizard uses
 *   - Audit log section — SOC 2 CC7.2 transparency for the client
 */
export default function SharePointIntakePage() {
  const [user, setUser] = useState(null);
  const [orgId, setOrgId] = useState(null);
  const [orgName, setOrgName] = useState('');
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/auth/session`, { credentials: 'include' });
        const data = await res.json();
        if (!data.success) {
          navigate('/login');
          return;
        }
        const allowed =
          data.user.user_type === 'platform_superuser' ||
          data.user.role === 'superadmin';
        if (!allowed) {
          navigate('/');
          return;
        }
        setUser(data.user);
        setOrgId(data.user.organization_id);
        setOrgName(data.user.organization_name || 'your organization');
      } catch (err) {
        navigate('/login');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const refreshEvents = useCallback(async () => {
    if (!orgId) return;
    try {
      const res = await fetch(`${API}/api/admin/v2/orgs/${orgId}/sharepoint-intake/events?limit=100`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setEvents(data.events || []);
    } catch {} finally {
      setEventsLoading(false);
    }
  }, [orgId]);

  useEffect(() => { refreshEvents(); }, [refreshEvents]);

  if (loading || !orgId) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="max-w-3xl mx-auto px-6 py-10">
        {/* Page header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
            <a href="/admin" className="hover:text-slate-300">Admin</a>
            <span>›</span>
            <span>SharePoint Intake</span>
          </div>
          <h1 className="text-2xl font-bold text-white">SharePoint Intake</h1>
          <p className="text-sm text-slate-400 mt-1">
            Connect one SharePoint folder. PSI processes any document dropped there — automatically, audited, revocable.
          </p>
        </div>

        {/* Connection card */}
        <SharePointConnectCard
          orgId={orgId}
          orgName={orgName}
          inviterName={user?.name}
          defaultAdminEmail={user?.email}
          onChange={() => refreshEvents()}
        />

        {/* Audit log */}
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-sm font-semibold text-white uppercase tracking-wide">Audit log</h2>
              <p className="text-xs text-slate-500 mt-0.5">SOC 2 CC7.2 · append-only · most recent first</p>
            </div>
            <button
              onClick={refreshEvents}
              className="text-xs text-slate-400 hover:text-white border border-slate-700 hover:border-slate-500 rounded-md px-2.5 py-1 transition"
            >
              Refresh
            </button>
          </div>
          {eventsLoading ? (
            <p className="text-sm text-slate-500">Loading audit log…</p>
          ) : events.length === 0 ? (
            <p className="text-sm text-slate-500 italic">No events yet.</p>
          ) : (
            <div className="rounded-2xl border border-slate-800 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-900 text-slate-400 text-[11px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">When</th>
                    <th className="text-left px-4 py-2 font-medium">Event</th>
                    <th className="text-left px-4 py-2 font-medium">Outcome</th>
                    <th className="text-left px-4 py-2 font-medium">Actor</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {events.map((e) => (
                    <tr key={e.id} className="text-slate-300">
                      <td className="px-4 py-2 text-xs text-slate-400 font-mono whitespace-nowrap">
                        {new Date(e.created_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-2">{prettifyEventType(e.event_type)}</td>
                      <td className="px-4 py-2">
                        {e.outcome === 'success' ? (
                          <span className="text-emerald-400 text-xs">success</span>
                        ) : (
                          <span className="text-red-400 text-xs" title={e.failure_reason || ''}>
                            failure{e.failure_reason ? ` · ${e.failure_reason}` : ''}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-400">
                        {e.actor_email || e.external_actor_email || <span className="italic text-slate-600">system</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function prettifyEventType(t) {
  if (!t) return '';
  return t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// Re-export StatusPill so callers from this module path resolve it if needed.
export { StatusPill };
